import { createServerFn } from "@tanstack/solid-start";
import { z } from "zod";
import { withSentrySpan } from "~/lib/sentry-span";
import { authFunctionMiddleware } from "~/server/middleware/auth";
import type { JsonValue } from "~/types/agent";
import { UpdateWorkflowConfigSchema } from "./workflow.schema";

function toSerializableWorkflowResult(result: unknown): JsonValue {
  if (
    result === null ||
    typeof result === "string" ||
    typeof result === "number" ||
    typeof result === "boolean"
  ) {
    return result;
  }

  if (Array.isArray(result)) {
    return result.map((item) => toSerializableWorkflowResult(item));
  }

  if (result && typeof result === "object") {
    const out: Record<string, JsonValue> = {};
    for (const [key, item] of Object.entries(
      result as Record<string, unknown>,
    )) {
      out[key] = toSerializableWorkflowResult(item);
    }
    return out;
  }

  return String(result);
}

const WorkflowStepsInputSchema = z.object({
  businessId: z.string().trim().min(1),
  entityType: z.string().trim().min(1),
  entityId: z.string().trim().min(1),
});

const WorkflowConfigInputSchema = z.object({
  businessId: z.string().trim().min(1),
  entityType: z.string().trim().min(1),
});

export const getWorkflowSteps = createServerFn({ method: "GET" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => WorkflowStepsInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("workflow.getSteps", async () => {
      const { getWorkflowSteps: get } = await import("./workflow.server");
      const rows = await get(
        context.user.id,
        data.businessId,
        data.entityType,
        data.entityId,
      );

      return rows.map((row) => ({
        ...row,
        result: toSerializableWorkflowResult(row.result),
      }));
    });
  });

export const getWorkflowConfig = createServerFn({ method: "GET" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => WorkflowConfigInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("workflow.getConfig", async () => {
      const { getWorkflowConfigForBusiness } =
        await import("./workflow.server");
      return getWorkflowConfigForBusiness(
        context.user.id,
        data.businessId,
        data.entityType,
      );
    });
  });

export const updateWorkflowConfig = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => UpdateWorkflowConfigSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("workflow.updateConfig", async () => {
      const { updateWorkflowConfigStep } = await import("./workflow.server");
      return updateWorkflowConfigStep(context.user.id, data.businessId, {
        businessId: data.businessId,
        entityType: data.entityType,
        step: data.step,
        enabled: data.enabled,
      });
    });
  });

const AllWorkflowConfigsInputSchema = z.object({
  businessId: z.string().trim().min(1),
});

export const getAllWorkflowConfigs = createServerFn({ method: "GET" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => AllWorkflowConfigsInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("workflow.getAllConfigs", async () => {
      const { getAllWorkflowConfigsForBusiness } =
        await import("./workflow.server");
      return getAllWorkflowConfigsForBusiness(context.user.id, data.businessId);
    });
  });
